import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PatientAppLayout from "../../components/PatientAppLayout/PatientAppLayout";
import API from "../../Api/Api";
import ENDPOINTS from "../../Api/Endpoints";
import { getSocket } from "../../Api/socket";
import { getPatientNotificationPath } from "../../utils/notificationRoutes";
import { SkeletonBlock, SkeletonText } from "../../components/Skeleton/Skeleton";
import { useInfiniteScrollLoad } from "../../hooks/useInfiniteScrollLoad";
import {
  getPatientAccessToken,
  VIEWED_STORAGE_KEY,
  normalizeNotificationListFromResponse,
  mergePrescriptionRejectedNotifications,
  PatientNotificationRow,
} from "../../components/PatientNotificationBell/PatientNotificationBell";
import "../../components/PatientNotificationBell/PatientNotificationBell.scss";
import "./PatientNotificationsPage.scss";

const PAGE_SIZE = 20;

function sortNotifications(list) {
  return [...list].sort((a, b) => {
    const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
    const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
    return tb - ta;
  });
}

function readPagination(res) {
  const body = res?.config != null && res.data !== undefined ? res.data : res;
  const p = body?.pagination;
  if (!p || typeof p !== "object") return { page: 1, limit: PAGE_SIZE, total: 0 };
  return {
    page: Math.max(1, Number(p.page) || 1),
    limit: Math.max(1, Number(p.limit) || PAGE_SIZE),
    total: Math.max(0, Number(p.total) || 0),
  };
}

export default function PatientNotificationsPage() {
  const navigate = useNavigate();
  const reduxToken = useSelector((s) => s.auth?.token);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const nextPageRef = useRef(1);
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);
  const rxItemsRef = useRef([]);
  const listBodyRef = useRef(null);

  const markNotificationsViewed = useCallback((notificationList) => {
    const now = Date.now();
    let viewedAt = now;
    if (Array.isArray(notificationList) && notificationList.length > 0) {
      const maxTs = notificationList.reduce((max, n) => {
        const t = n.updatedAt || n.createdAt;
        if (!t) return max;
        const ts = new Date(t).getTime();
        return isNaN(ts) ? max : Math.max(max, ts);
      }, 0);
      viewedAt = Math.max(now, maxTs + 1);
    }
    try {
      localStorage.setItem(VIEWED_STORAGE_KEY, String(viewedAt));
    } catch (_) {}
    try {
      window.dispatchEvent(new CustomEvent("healbid_patient_notifications_viewed", { detail: viewedAt }));
    } catch (_) {}
  }, []);

  const fetchPage = useCallback(
    async ({ reset, markViewed }) => {
      if (!getPatientAccessToken(reduxToken)) {
        setNotifications([]);
        setHasMore(false);
        hasMoreRef.current = false;
        return;
      }

      if (!reset) {
        if (loadingMoreRef.current || !hasMoreRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
      } else {
        nextPageRef.current = 1;
        hasMoreRef.current = true;
        setHasMore(true);
        setLoading(true);
      }

      const targetPage = nextPageRef.current;

      try {
        if (reset) {
          rxItemsRef.current = [];
          try {
            const rxRes = await API.get(ENDPOINTS.GET_MY_PRESCRIPTIONS, {
              params: { page: 1, limit: 100 },
            });
            const rxBody = rxRes?.data ?? {};
            rxItemsRef.current = Array.isArray(rxBody.items) ? rxBody.items : [];
          } catch (rxErr) {
            console.warn("Patient prescriptions merge skipped:", rxErr?.message || rxErr);
          }
        }

        const notifRes = await API.get(ENDPOINTS.PATIENT_NOTIFICATIONS, {
          params: { page: targetPage, limit: PAGE_SIZE },
        });
        const apiList = normalizeNotificationListFromResponse(notifRes);
        const { total } = readPagination(notifRes);

        const pageItems =
          reset || targetPage === 1
            ? mergePrescriptionRejectedNotifications(apiList, rxItemsRef.current)
            : apiList;

        setNotifications((prev) => {
          const next = sortNotifications(reset ? pageItems : [...prev, ...pageItems]);
          if (reset && markViewed) markNotificationsViewed(next);
          return next;
        });

        const loadedThrough = targetPage * PAGE_SIZE;
        const more = total > 0 ? loadedThrough < total : apiList.length >= PAGE_SIZE;
        hasMoreRef.current = more;
        setHasMore(more);
        nextPageRef.current = targetPage + 1;
      } catch (err) {
        console.error("Patient notifications page fetch error:", err);
        if (reset) {
          setNotifications([]);
          setHasMore(false);
          hasMoreRef.current = false;
        }
      } finally {
        if (reset) setLoading(false);
        else {
          loadingMoreRef.current = false;
          setLoadingMore(false);
        }
      }
    },
    [markNotificationsViewed, reduxToken]
  );

  const loadMore = useCallback(() => {
    void fetchPage({ reset: false, markViewed: false });
  }, [fetchPage]);

  const sentinelRef = useInfiniteScrollLoad({
    hasMore,
    loading,
    loadingMore,
    onLoadMore: loadMore,
    rootRef: listBodyRef,
  });

  useEffect(() => {
    if (!getPatientAccessToken(reduxToken)) return;
    void fetchPage({ reset: true, markViewed: true });
  }, [fetchPage, reduxToken]);

  useEffect(() => {
    const onPatientSignal = () => {
      if (!getPatientAccessToken(reduxToken)) return;
      void fetchPage({ reset: true, markViewed: false });
    };
    window.addEventListener("healbid:patient-notification", onPatientSignal);
    return () => window.removeEventListener("healbid:patient-notification", onPatientSignal);
  }, [fetchPage, reduxToken]);

  useEffect(() => {
    if (!getPatientAccessToken(reduxToken)) return;

    let pollId = null;
    const startPolling = () => {
      if (pollId) return;
      pollId = window.setInterval(() => {
        try {
          void fetchPage({ reset: true, markViewed: false });
        } catch (_) {}
      }, 10000);
    };
    const stopPolling = () => {
      if (!pollId) return;
      window.clearInterval(pollId);
      pollId = null;
    };

    const socket = getSocket({ role: "patient" });
    if (!socket) {
      startPolling();
      return () => stopPolling();
    }

    const onNotification = () => {
      try {
        void fetchPage({ reset: true, markViewed: false });
      } catch (_) {}
    };
    const onBidUpdate = () => {
      try {
        void fetchPage({ reset: true, markViewed: false });
      } catch (_) {}
    };
    socket.on("notification:new", onNotification);
    socket.on("bid:update", onBidUpdate);
    socket.on("connect", stopPolling);
    socket.on("disconnect", startPolling);
    socket.on("connect_error", startPolling);
    if (!socket.connected) startPolling();
    return () => {
      try {
        socket.off("notification:new", onNotification);
        socket.off("bid:update", onBidUpdate);
        socket.off("connect", stopPolling);
        socket.off("disconnect", startPolling);
        socket.off("connect_error", startPolling);
      } catch (_) {}
      stopPolling();
    };
  }, [fetchPage, reduxToken]);

  const onRowClick = useCallback(
    (n) => {
      const path = getPatientNotificationPath(n) || "/HealBidDashboard";
      navigate(path);
    },
    [navigate]
  );

  return (
    <PatientAppLayout activeNav="notifications">
      <div className="patientNotificationsPage patientNotificationsPage--feed">
        <h1 className="patientNotificationsPage__srTitle">Notifications</h1>
        <div className="patientNotificationsPage__card">
          <div
            ref={listBodyRef}
            className="patient_notification_popup_body patientNotificationsPage__body"
          >
            {loading ? (
              <ul className="patient_notification_list" aria-label="Loading notifications">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <li key={`patient-notif-page-skel-${idx}`} className="patient_notification_item">
                    <div className="patient_notification_item__inner">
                      <div className="patient_notification_item__media">
                        <SkeletonBlock height={44} width={44} className="patient_notification_item__skelRing" />
                      </div>
                      <div className="patient_notification_item__main">
                        <div className="patient_notification_item_title">
                          <SkeletonBlock height={14} width="55%" />
                        </div>
                        <div className="patient_notification_item_body" style={{ marginTop: 8 }}>
                          <SkeletonText lines={2} lineHeight={12} gap={8} />
                        </div>
                        <div className="patient_notification_item__footer" style={{ marginTop: 8 }}>
                          <SkeletonBlock height={10} width="32%" />
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : notifications.length === 0 ? (
              <div className="patient_notification_empty">No notifications</div>
            ) : (
              <>
                <ul className="patient_notification_list">
                  {notifications.map((n, i) => (
                    <PatientNotificationRow
                      key={`${n.type}-${n.messageId || n.bidLogId || n.appointmentId || n.prescriptionId || n.matchRequestId || ""}-${i}`}
                      notification={n}
                      unread={false}
                      compactCopy
                      onRowActivate={() => onRowClick(n)}
                    />
                  ))}
                </ul>
                {hasMore || loadingMore ? (
                  <div
                    ref={sentinelRef}
                    className="patientNotificationsPage__loadMore"
                    aria-live="polite"
                  >
                    {loadingMore ? "Loading more…" : ""}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </PatientAppLayout>
  );
}
